import axios from 'axios'
import { storage } from '@/utils/storage'

export const TOKEN_KEY = 'edge_token'

const api = axios.create({
  baseURL: (process.env.VUE_APP_API_BASE || '').replace(/\/$/, ''),
  timeout: 20000,
  headers: { Accept: 'application/json' }
})

// Called on 401 so the router can send the user back to login.
let onUnauthorized = () => {}
export function setUnauthorizedHandler (fn) {
  onUnauthorized = fn
}

export class ApiError extends Error {
  constructor (message, { code = '', status = 0, errors = null } = {}) {
    super(message)
    this.code = code
    this.status = status
    this.errors = errors
  }
}

// Endpoints marked "无需登录" in the API doc — these must never carry a Bearer token,
// even if a (possibly stale) token is still in storage. Keep in sync with the doc.
const PUBLIC_PATHS = new Set([
  '/api/news/banner-list',
  '/api/global/usernameOTP',
  '/api/global/checkUsernameOtp',
  '/api/global/sent-otp',
  '/api/global/check-otp',
  '/api/global/country_list',
  '/api/global/fiatList',
  '/api/global/lookup',
  '/api/auth/login',
  '/api/auth/signup',
  '/api/auth/logout',
  '/api/auth/reset-password',
  '/api/auth/checkRefAddress',
  '/api/auth/walletLogin'
])

export function isPublicPath (url = '') {
  const path = url.replace(/^https?:\/\/[^/]+/, '').split('?')[0].replace(/\/+$/, '')
  return PUBLIC_PATHS.has(path)
}

api.interceptors.request.use(config => {
  if (isPublicPath(config.url)) {
    delete config.headers.Authorization
    return config
  }
  const token = storage.get(TOKEN_KEY)
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

// Member API envelope: { data, error_code: "0" | "E..", message }.
// Some endpoints ("raw") return the payload directly without the envelope.
api.interceptors.response.use(
  res => {
    const body = res.data
    if (body && typeof body === 'object' && 'error_code' in body) {
      if (String(body.error_code) !== '0') {
        return Promise.reject(new ApiError(body.message || 'REQUEST_FAILED', { code: body.error_code, status: res.status }))
      }
      return body.data
    }
    return body
  },
  err => {
    const res = err.response
    if (!res) return Promise.reject(new ApiError('NETWORK_ERROR'))

    const body = res.data || {}
    if (res.status === 401) {
      storage.remove(TOKEN_KEY)
      onUnauthorized()
      return Promise.reject(new ApiError(body.message || 'UNAUTHORIZED', { status: 401 }))
    }
    if (res.status === 422 && body.errors) {
      const first = Object.values(body.errors)[0]
      return Promise.reject(new ApiError(Array.isArray(first) ? first[0] : body.message, { status: 422, errors: body.errors }))
    }
    if (res.status === 429) {
      return Promise.reject(new ApiError('DUPLICATE_REQUEST', { status: 429 }))
    }
    return Promise.reject(new ApiError(body.message || 'SERVER_ERROR', { code: body.error_code, status: res.status }))
  }
)

// Backend expects form-encoded bodies for POST.
export function toForm (data = {}) {
  const form = new URLSearchParams()
  Object.keys(data).forEach(k => {
    if (data[k] !== undefined && data[k] !== null) form.append(k, data[k])
  })
  return form
}

export default api
